import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import serviceOrdersApi from '../../../api/serviceOrdersApi';
import servicesApi from '../../../api/servicesApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import { SERVICE_ORDER_STATUS_COLORS, serviceOrderStatusLabel } from '../../../utils/serviceOrderStatuses';
import ServiceOrderFormDialog from '../../../sections/admin/service-orders/ServiceOrderFormDialog';

// Enlace wa.me al telefono del cliente: un numero peruano de 9 digitos se completa con el
// codigo de pais (51); si ya viene con codigo de pais se usa tal cual. null si no hay telefono.
const clientWhatsAppLink = (order) => {
  const digits = (order.client?.phone || '').replace(/\D/g, '');
  if (!digits) {
    return null;
  }
  const number = digits.length === 9 ? `51${digits}` : digits;
  const message = `Hola ${order.client?.name || ''}, te escribimos de PalaciComputers por tu solicitud de servicio técnico N.° ${order.id}${
    order.service?.name ? ` (${order.service.name})` : ''
  }.`;
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
};

export default function ServiceOrdersListPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [services, setServices] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce del buscador para no disparar una peticion en cada tecla
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [servicesRes, statusesRes, technosRes] = await Promise.all([
          servicesApi.getServices({ per_page: 100 }),
          serviceOrdersApi.getServiceOrderStatuses(),
          serviceOrdersApi.getTechnicians(),
        ]);
        setServices(servicesRes.data.data);
        setStatuses(statusesRes.data.data);
        setTechnicians(technosRes.data.data);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar los catálogos de servicio.'), {
          variant: 'error',
        });
      }
    };

    loadCatalogs();
  }, [enqueueSnackbar]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await serviceOrdersApi.getServiceOrders({
        search: search || undefined,
        status: statusFilter || undefined,
        service_id: serviceFilter || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      });
      setRows(data.data);
      setRowCount(data.meta.total);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de órdenes de servicio.'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, serviceFilter, paginationModel, enqueueSnackbar]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleNew = () => {
    setEditingOrder(null);
    setFormOpen(true);
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormOpen(true);
  };

  const handleFormSaved = () => {
    setFormOpen(false);
    fetchOrders();
  };

  // Abre el chat de WhatsApp del cliente y, si la orden seguia "pendiente", la pasa a
  // "contacto" (el backend no toca las ordenes que ya avanzaron mas alla).
  const handleWhatsApp = async (order) => {
    const link = clientWhatsAppLink(order);
    if (!link) {
      enqueueSnackbar('Este cliente no tiene un teléfono registrado.', { variant: 'warning' });
      return;
    }
    // Se abre antes de la peticion para que el navegador no lo bloquee como ventana emergente.
    window.open(link, '_blank', 'noopener,noreferrer');

    if (order.status?.name !== 'pendiente') {
      return;
    }
    try {
      await serviceOrdersApi.markServiceOrderContacted(order.id);
      enqueueSnackbar('La orden pasó a "Contacto".', { variant: 'success' });
      fetchOrders();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo actualizar el estado de la orden.'), { variant: 'error' });
    }
  };

  // Las columnas se memorizan una sola vez: el ref les da siempre la version actual del handler
  // (con el fetchOrders de los filtros vigentes).
  const handleWhatsAppRef = useRef(handleWhatsApp);
  handleWhatsAppRef.current = handleWhatsApp;

  const handleConfirmDelete = async () => {
    if (!orderToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await serviceOrdersApi.deleteServiceOrder(orderToDelete.id);
      enqueueSnackbar('Orden de servicio eliminada correctamente.', { variant: 'success' });
      setOrderToDelete(null);
      fetchOrders();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar la orden de servicio.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'client',
        headerName: 'Cliente',
        flex: 1,
        minWidth: 180,
        valueGetter: (value, row) => row.client?.name || '—',
      },
      {
        field: 'phone',
        headerName: 'Teléfono',
        width: 130,
        valueGetter: (value, row) => row.client?.phone || '—',
      },
      {
        field: 'service',
        headerName: 'Servicio',
        flex: 1,
        minWidth: 180,
        valueGetter: (value, row) => row.service?.name || '—',
      },
      {
        field: 'service_type',
        headerName: 'Tipo',
        width: 150,
        valueGetter: (value, row) => row.service?.service_type?.name || '—',
      },
      {
        field: 'technician',
        headerName: 'Técnico',
        width: 160,
        valueGetter: (value, row) => row.technician?.name || 'Sin asignar',
      },
      {
        field: 'status',
        headerName: 'Estado',
        width: 130,
        renderCell: (params) => (
          <Chip
            label={serviceOrderStatusLabel(params.value?.name)}
            color={SERVICE_ORDER_STATUS_COLORS[params.value?.name] || 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'received_at',
        headerName: 'Registrado',
        width: 130,
        valueFormatter: (value) => (value ? new Date(value).toLocaleDateString('es-PE') : '—'),
      },
      {
        field: 'finished_at',
        headerName: 'Entregado',
        width: 130,
        valueFormatter: (value) => (value ? new Date(value).toLocaleDateString('es-PE') : '—'),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 130,
        getActions: (params) => [
          <GridActionsCellItem
            key="whatsapp"
            icon={<WhatsAppIcon color="success" />}
            label="Escribir por WhatsApp"
            onClick={() => handleWhatsAppRef.current(params.row)}
          />,
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Editar"
            onClick={() => handleEdit(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => setOrderToDelete(params.row)}
          />,
        ],
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }} direction="row">
        <div>
          <Typography variant="h4">Órdenes de servicio</Typography>
          <Typography variant="body2" color="text.secondary">
            Recepción y seguimiento de equipos en mantenimiento, reparación o soporte técnico.
          </Typography>
        </div>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Nueva orden
        </Button>
      </Stack>

      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Cliente o descripción"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              label="Estado"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {statuses.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {serviceOrderStatusLabel(s.name)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              label="Servicio"
              value={serviceFilter}
              onChange={(e) => {
                setServiceFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {services.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
        />
      </Card>

      <ServiceOrderFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={handleFormSaved}
        order={editingOrder}
        services={services}
        statuses={statuses}
        technicians={technicians}
      />

      <Dialog open={Boolean(orderToDelete)} onClose={() => setOrderToDelete(null)}>
        <DialogTitle>Eliminar orden de servicio</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar la orden de <strong>{orderToDelete?.client?.name}</strong>? Esta acción se
            puede revertir solo desde la base de datos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
